import React, { FC, useEffect, useState } from "react"
import _ from 'lodash'
// Components
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import DropdownInput from "components/inputs/dropdown"
// Data
import { alphanum } from "components/table/lib/sort"
import api_match from "data/interface/api"
import propertyReportService from "data/reports-property"
import assetService from "data/asset-list"
import portfolioService from "data/portfolio"
// Styles
import { Button_s, Label_s } from "styles/app-styles"
import DataTable_s from 'components/table/data-table-styles'
import { ReportForm_s } from "styles/report-styles"
import { PortfolioDetail_s } from 'components/asset-management/reports/report-styles'
import { FormError_s } from "styles/form-styles"

const AssetDetails: FC = () => {
  const [data, setData] = useState<object[]>([])
  const [initSort, setInitSort] = useState<boolean>(false)
  const [assets, setAssets] = useState<object[]|null>(null);
  const [portfolios, setPortfolios] = useState<object[]|null>(null);
  const [selectionMethod, setSelectionMethod] = useState<string>('portfolios')

  useEffect(() => {
    const assetSubscription = assetService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            const assets = [...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")))
            setAssets(assets)
            setInitSort(true)
          } else {
            setAssets(ok.value)
          }
        },
        err   => {
            setAssets(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const portfolioSubscription = portfolioService.stream().subscribe(data => {
      api_match(
        data,
        init  => setPortfolios(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            const portfolios = [...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")))
            setPortfolios(portfolios)
            setData(portfolios)
            setInitSort(true)
          } else {
            setPortfolios(ok.value)
            setData(ok.value)
          }
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    assetService.fetchAssets()
    portfolioService.fetchPortfolios()

    return function cleanup() {
      assetSubscription.unsubscribe()
      portfolioSubscription.unsubscribe()
    }
  }, [initSort])

  useEffect(() => {
    if (selectionMethod === 'portfolios' && portfolios) setData(portfolios)
    else if (assets) setData(assets)
  }, [selectionMethod])

  function submit():void {
    let params = { selection_method: selectionMethod === 'portfolios' ? 'portfolio' : 'asset' }
    if (selectionMethod === 'portfolios'){ params['portfolios'] = data?.map(portfolio => portfolio['portfolioid']) }
    else params['assets'] = data?.map(asset => asset['assetid'])
    propertyReportService.propertyReport({
      report: 'asset_details',
      params
    });
  }

  return (
    <PortfolioDetail_s>
      <ReportForm_s>
        <div style={{width: '100%'}}><Button_s style={{width: '92px'}} primary disabled={data.length < 1} onClick={() => submit()}>Run Report</Button_s></div>
        <div>
          {!data && <FormError_s>Please select an asset or chart to run report</FormError_s>}
          <Label_s>Selection Method</Label_s>
          <DropdownInput
            persist={(field:string, val:string)=>setSelectionMethod(val)}
            field="periodicity"
            options={[{label: 'Assets', value: 'assets'},{label: 'Portfolios', value: 'portfolios'}] || []}
            value={selectionMethod}
          />
        </div>
        { selectionMethod === 'assets' && <div>
          <Label_s>Select Assets</Label_s>
          { data.length < 1 && <FormError_s style={{width: '240px'}}>Please select an asset to run report</FormError_s> }
          <DataTable_s>
            <DataTable
              value={assets || undefined}
              paginator={true}
              rows={20}
              rowsPerPageOptions={[10, 20, 50, 100, 500]}
              emptyMessage="No records found"
              selection={data}
              onSelectionChange={(e)=>setData(e.value)}
            >
              <Column selectionMode="multiple" style={{width: '20px'}} />
              <Column field="code" header="Code" filter filterMatchMode="contains" sortable={true} style={{width: '150px'}} />
              <Column field="name" header="Name" filter filterMatchMode="contains" sortable={true} style={{width: '150px'}} />
            </DataTable>
          </DataTable_s>
        </div> }
        { selectionMethod === 'portfolios' && <div>
          <Label_s>Select Portfolios</Label_s>
          { data.length < 1 && <FormError_s style={{width: '240px'}}>Please select a portfolio to run report</FormError_s> }
          <DataTable_s>
            <DataTable
              value={portfolios || undefined}
              paginator={true}
              rows={20}
              rowsPerPageOptions={[10, 20, 50, 100, 500]}
              emptyMessage="No records found"
              selection={data}
              onSelectionChange={(e)=>setData(e.value)}
            >
              <Column selectionMode="multiple" style={{width: '20px'}} />
              <Column field="code" header="Code" filter filterMatchMode="contains" sortable={true} style={{width: '150px'}} />
              <Column field="name" header="Name" filter filterMatchMode="contains" sortable={true} style={{width: '150px'}} />
            </DataTable>
          </DataTable_s>
        </div> }
      </ReportForm_s>
    </PortfolioDetail_s>
  )
}

export default AssetDetails

